// Approval transport slice. One exact path/body per review intent; authenticated Actor resolution and every
// state/authority/CAS rule remain server-owned. Split from api-client.ts to keep the main transport composer
// below its modular size ceiling.

import type { SurfaceApi, RouteResult } from './api-client.ts';
import type { ApprovalGateKind } from './projections.ts';
import { API_PREFIX, postIntent } from './post-intent.ts';

/** The POST path each Gate kind routes to (one route per owning Gate). */
const GATE_PATH: Readonly<Record<ApprovalGateKind, string>> = {
  task_draft: `${API_PREFIX}/approvals/task-draft`,
  task_start: `${API_PREFIX}/approvals/task-start`,
  metric_activation: `${API_PREFIX}/approvals/metric-activate`,
  schedule_activation: `${API_PREFIX}/approvals/schedule-activate`,
  metric_value_authoritative: `${API_PREFIX}/approvals/metric-value-approve`,
  hubspot_write: `${API_PREFIX}/approvals/hubspot-write`,
};

/** One owner-module decline route per Gate kind. */
const DECLINE_PATH: Readonly<Record<ApprovalGateKind, string>> = {
  task_draft: `${API_PREFIX}/approvals/task-draft-decline`,
  task_start: `${API_PREFIX}/approvals/task-start-decline`,
  metric_activation: `${API_PREFIX}/approvals/metric-activate-decline`,
  schedule_activation: `${API_PREFIX}/approvals/schedule-activate-decline`,
  metric_value_authoritative: `${API_PREFIX}/approvals/metric-value-decline`,
  hubspot_write: `${API_PREFIX}/approvals/hubspot-write-decline`,
};

/** Dedicated contracts carry their owning durable id; the other approval routes carry `objectId`. */
function postApprovalGate(
  kind: ApprovalGateKind,
  objectId: string,
  expectedRevision: number | null,
): Promise<RouteResult> {
  if (
    kind === 'schedule_activation' &&
    (expectedRevision === null || !Number.isSafeInteger(expectedRevision) || expectedRevision < 1)
  ) {
    return Promise.resolve({ ok: false, note: 'Refresh this schedule before activating it.' });
  }
  const identifier =
    kind === 'schedule_activation'
      ? { scheduleId: objectId, expectedRevision }
      : kind === 'hubspot_write'
        ? { proposalId: objectId }
        : { objectId };
  const body = expectedRevision === null ? identifier : { ...identifier, expectedRevision };
  return postIntent(GATE_PATH[kind], body);
}

/** Compose the approval exercise/decline/change-request legs into the main SurfaceApi. */
export function httpApprovalApi(): Pick<
  SurfaceApi,
  'exerciseGate' | 'declineGate' | 'requestScheduleChanges' | 'activateSchedule'
> {
  return {
    exerciseGate: postApprovalGate,
    declineGate: (kind, objectId, reason, expectedRevision) =>
      postIntent(
        DECLINE_PATH[kind],
        kind === 'hubspot_write' ? { objectId, reason } : { objectId, reason, expectedRevision },
      ),
    requestScheduleChanges: (scheduleId, expectedRevision, reason) =>
      postIntent(`${API_PREFIX}/approvals/schedule-activate-request-changes`, {
        objectId: scheduleId,
        expectedRevision,
        reason,
      }),
    activateSchedule: (scheduleId, expectedRevision) =>
      postIntent(`${API_PREFIX}/approvals/schedule-activate`, { scheduleId, expectedRevision }),
  };
}
